/**
 * CteAddMenu — a three-dot (kebab) menu whose single "Add CTE" action opens the
 * CTE add form. It replaces the always-visible empty "With" card in the compact
 * surfaces (the compact main-query popover header + an inline `col IN (SELECT …)`
 * subquery's SELECT header). The empty-state card wasted vertical space, so
 * adding a CTE now hides behind a kebab that callers mount ONLY when the query
 * has no CTE yet.
 *
 * Composition: a shadcn `<DropdownMenu>` kebab with one `<DropdownMenuItem>`;
 * selecting it opens a CONTROLLED `<CteAddPopover hideTrigger>` anchored beside
 * the kebab. The dropdown's `onCloseAutoFocus` is prevented so closing the menu
 * doesn't yank focus back from the popover's autofocused name input (avoids a
 * focus regression between the two stacked Radix overlays).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove/rename CTE.
 */

import { useState } from 'react';
import { MoreHorizontal, Plus } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { VqeEditContext } from '../../edit/editContext';
import { CteAddPopover } from './CteAddPopover';

export interface CteAddMenuProps {
  readonly editContext: VqeEditContext;
  /**
   * Menu-item label — "Add CTE" for an inline subquery, "Add CTE to the query"
   * for the compact main query. Callers gate the mount on "no CTE yet", so this
   * component always offers the add action.
   */
  readonly label: string;
}

export function CteAddMenu({ editContext, label }: CteAddMenuProps) {
  const [formOpen, setFormOpen] = useState(false);
  const { track } = useAnalytics();

  return (
    <span className="relative inline-flex">
      <DropdownMenu
        onOpenChange={(open) => {
          if (open) track('feature:vqe_cte_menu_open');
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground"
            data-testid="vqe-cte-menu"
            aria-label={label}
          >
            <MoreHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="text-xs"
          // Don't pull focus back to the trigger when the menu closes — the
          // controlled add form below claims focus (its name input autofocuses).
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuItem data-testid="vqe-cte-menu-add" onSelect={() => setFormOpen(true)}>
            <Plus className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            {label}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CteAddPopover
        editContext={editContext}
        open={formOpen}
        onOpenChange={setFormOpen}
        hideTrigger
      />
    </span>
  );
}
